import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { ClientEmailSwitch } from "../components/clients/ClientEmailSwitch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, Plus, PencilSimple, Trash, User, Envelope, Phone, Buildings, Briefcase, ClipboardText, CurrencyGbp, Wrench, Clock, ChartBar, ArrowUp, ArrowDown, File as FileIcon, UploadSimple, DownloadSimple, FolderOpen, Globe, Receipt, Eye, Folder as FolderIcon, Hash, Prohibit, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import AddressAutocomplete from "../components/AddressAutocomplete";
import AcknowledgmentEmailEditor from "../components/AcknowledgmentEmailEditor";
import ClientPortalUsersPanel from "../components/ClientPortalUsersPanel";
import ClientPropertiesPanel from "../components/ClientPropertiesPanel";
import ClientBrandingPanel from "../components/ClientBrandingPanel";
import SubClientsPanel from "../components/SubClientsPanel";
import ClientHsePackDialog from "../components/hse/ClientHsePackDialog";
import { useAuth } from "../contexts/AuthContext";

const EMPTY_CONTACT = { first_name: "", last_name: "", job_role: "", email: "", phone: "", color: "#3b82f6" };

const TAG_COLORS = [
  { value: "#3b82f6", label: "Blue" },
  { value: "#8b5cf6", label: "Purple" },
  { value: "#ec4899", label: "Pink" },
  { value: "#ef4444", label: "Red" },
  { value: "#f97316", label: "Orange" },
  { value: "#eab308", label: "Yellow" },
  { value: "#22c55e", label: "Green" },
  { value: "#14b8a6", label: "Teal" },
  { value: "#64748b", label: "Slate" },
];

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ ...EMPTY_CONTACT });
  const [savingContact, setSavingContact] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [clientForm, setClientForm] = useState({});
  const [savingClient, setSavingClient] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [financial, setFinancial] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [showHsePack, setShowHsePack] = useState(false);
  const [linkedFolders, setLinkedFolders] = useState([]);
  const [linkedFoldersLoading, setLinkedFoldersLoading] = useState(true);
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docCategory, setDocCategory] = useState("other");
  const [docNotes, setDocNotes] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const docFileRef = useRef(null);

  // Client Messages state
  const [clientMessages, setClientMessages] = useState([]);
  const [clientMsgLoading, setClientMsgLoading] = useState(false);
  const [adminReply, setAdminReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const { user: currentUser } = useAuth();
  const isAdmin = ["super_admin", "admin", "project_manager"].includes(currentUser?.role);

  const fetchClient = useCallback(async () => {
    try {
      const { data } = await API.get(`/clients/${clientId}`);
      setClient(data);
    } catch { toast.error("Failed to load client"); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchClient(); }, [fetchClient]);

  useEffect(() => {
    setTimelineLoading(true);
    API.get(`/clients/${clientId}/activity`).then(({ data }) => {
      setTimeline(data.timeline || []);
      setFinancial(data.financial || null);
    }).catch(() => {}).finally(() => setTimelineLoading(false));
  }, [clientId]);

  // Lifetime invoice billing summary for this client
  const [clientInvoices, setClientInvoices] = useState([]);
  useEffect(() => {
    API.get(`/invoices`, { params: { client_id: clientId, limit: 200 } })
      .then(({ data }) => setClientInvoices(data.invoices || []))
      .catch(() => {});
  }, [clientId]);

  const fetchDocuments = useCallback(async () => {
    setDocsLoading(true);
    try {
      const { data } = await API.get(`/clients/${clientId}/documents`);
      setDocuments(data.documents || []);
    } catch { /* ignore */ }
    finally { setDocsLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const fetchLinkedFolders = useCallback(async () => {
    setLinkedFoldersLoading(true);
    try {
      const { data } = await API.get(`/client-folders/${clientId}`);
      setLinkedFolders(data.folders || []);
    } catch { /* ignore — silent empty state */ }
    finally { setLinkedFoldersLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchLinkedFolders(); }, [fetchLinkedFolders]);

  const fetchClientMessages = useCallback(async () => {
    setClientMsgLoading(true);
    try {
      const { data } = await API.get(`/clients/${clientId}/messages`);
      setClientMessages(data.messages || []);
    } catch { /* ignore */ }
    finally { setClientMsgLoading(false); }
  }, [clientId]);

  useEffect(() => { if (client) fetchClientMessages(); }, [client, fetchClientMessages]);

  const handleSendAdminReply = async () => {
    if (!adminReply.trim()) return;
    setSendingReply(true);
    try {
      await API.post(`/clients/${clientId}/messages`, { text: adminReply.trim() });
      setAdminReply("");
      fetchClientMessages();
    } catch { toast.error("Failed to send message"); }
    finally { setSendingReply(false); }
  };

  const handleTogglePortal = async (enabled) => {
    try {
      await API.put(`/client-portal/toggle/${clientId}`, { portal_enabled: enabled });
      toast.success(enabled ? "Client portal enabled" : "Client portal disabled — portal users can no longer sign in");
      fetchClient();
    } catch { toast.error("Failed to toggle portal"); }
  };

  const handleToggleAutoInvoice = async (enabled) => {
    try {
      await API.put(`/clients/${clientId}`, { auto_invoice: enabled });
      toast.success(enabled ? "Auto-invoicing enabled" : "Auto-invoicing disabled");
      fetchClient();
    } catch { toast.error("Failed to toggle auto-invoice"); }
  };

  const handleToggleContractorDefault = async (enabled) => {
    try {
      await API.put(`/clients/${clientId}`, { show_contractor_default: enabled });
      toast.success(enabled ? "This client will see contractor names by default" : "Contractor names hidden by default for this client");
      fetchClient();
    } catch { toast.error("Failed to update contractor visibility"); }
  };

  const handleTogglePoRequired = async (enabled) => {
    try {
      await API.put(`/clients/${clientId}`, { po_required: enabled });
      toast.success(enabled ? "PO/WO now required — jobs can't be invoiced until a client PO/WO number is entered (or waived)" : "PO/WO no longer required for invoicing");
      fetchClient();
    } catch { toast.error("Failed to update PO rule"); }
  };

  const handleTogglePoExempt = async (enabled) => {
    try {
      await API.put(`/clients/${clientId}`, { po_exempt: enabled });
      toast.success(enabled ? `${client.company_name || "This sub-client"} is exempt — its jobs can be invoiced without a PO/WO` : "Exemption removed — the main client's PO/WO rule applies again");
      fetchClient();
    } catch { toast.error("Failed to update PO exemption"); }
  };

  const handleUploadDoc = async () => {
    if (!docFile) { toast.error("Select a file first"); return; }
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append("file", docFile);
      const { data: uploaded } = await API.post(`/upload?entity_type=client_document&entity_id=${clientId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await API.post(`/clients/${clientId}/documents`, {
        file_id: uploaded.file_id || uploaded.id,
        file_name: uploaded.file_name,
        file_url: uploaded.file_url,
        content_type: uploaded.content_type,
        size: uploaded.size,
        category: docCategory,
        notes: docNotes.trim(),
      });
      toast.success("Document uploaded");
      setShowUploadDoc(false);
      setDocFile(null);
      setDocCategory("other");
      setDocNotes("");
      fetchDocuments();
    } catch { toast.error("Upload failed"); }
    finally { setUploadingDoc(false); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await API.delete(`/clients/${clientId}/documents/${docId}`);
      toast.success("Document deleted");
      fetchDocuments();
    } catch { toast.error("Failed to delete"); }
  };

  const handleUpdateDoc = async () => {
    if (!editingDoc) return;
    try {
      await API.put(`/clients/${clientId}/documents/${editingDoc.id}`, {
        category: editingDoc.category,
        notes: editingDoc.notes,
      });
      toast.success("Document updated");
      setEditingDoc(null);
      fetchDocuments();
    } catch { toast.error("Failed to update"); }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!contactForm.first_name.trim() || !contactForm.last_name.trim()) { toast.error("First and last name are required"); return; }
    setSavingContact(true);
    try {
      if (editingContact) {
        await API.put(`/clients/${clientId}/contacts/${editingContact.id}`, contactForm);
        toast.success("Contact updated");
      } else {
        await API.post(`/clients/${clientId}/contacts`, contactForm);
        toast.success("Contact added");
      }
      setShowAddContact(false);
      setEditingContact(null);
      setContactForm({ ...EMPTY_CONTACT });
      fetchClient();
    } catch { toast.error("Failed to save contact"); }
    finally { setSavingContact(false); }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Remove this contact?")) return;
    try {
      await API.delete(`/clients/${clientId}/contacts/${contactId}`);
      toast.success("Contact removed");
      fetchClient();
    } catch { toast.error("Failed to remove contact"); }
  };

  const openEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({ first_name: contact.first_name, last_name: contact.last_name, job_role: contact.job_role || "", email: contact.email || "", phone: contact.phone || "", color: contact.color || "#3b82f6" });
    setShowAddContact(true);
  };

  const handleEditClient = async (e) => {
    e.preventDefault();
    setSavingClient(true);
    try {
      await API.put(`/clients/${clientId}`, clientForm);
      toast.success("Client updated");
      setShowEditClient(false);
      fetchClient();
    } catch { toast.error("Failed to update client"); }
    finally { setSavingClient(false); }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;
  if (!client) return <div className="text-center py-12 text-muted-foreground">Client not found</div>;

  const contacts = client.contacts || [];

  return (
    <div data-testid="client-detail-page">
      <Link to="/clients" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Back to Clients
      </Link>

      {/* Client Header */}
      <div className="border border-border rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xl font-bold shrink-0">
              {(client.company_name || client.first_name || "?")[0]?.toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-bold font-[Chivo] tracking-tight" data-testid="client-name">
                {client.first_name} {client.last_name}
              </h1>
              {client.company_name && (
                <p className="text-base text-muted-foreground flex items-center gap-1.5 mt-0.5" data-testid="client-company">
                  <Buildings size={14} /> {client.company_name}
                </p>
              )}
              {client.job_role && (
                <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
                  <Briefcase size={14} /> {client.job_role}
                </p>
              )}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => {
            setClientForm({ first_name: client.first_name, last_name: client.last_name, company_name: client.company_name || "", job_role: client.job_role || "", company_address: client.company_address || "", company_street: client.company_street || "", company_town: client.company_town || "", company_county: client.company_county || "", company_postcode: client.company_postcode || "", company_lat: client.company_lat || null, company_lng: client.company_lng || null, email: client.email || "", phone: client.phone || "" });
            setShowEditClient(true);
          }} data-testid="edit-client-btn">
            <PencilSimple size={14} className="mr-1" /> Edit
          </Button>
        </div>

        {/* Contact Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
          {client.email && (
            <div className="flex items-center gap-2 border border-border rounded-md px-3 py-2">
              <Envelope size={16} className="text-muted-foreground shrink-0" />
              <a href={`mailto:${client.email}`} className="text-sm hover:underline truncate" data-testid="client-email-display">{client.email}</a>
            </div>
          )}
          {client.phone && (
            <div className="flex items-center gap-2 border border-border rounded-md px-3 py-2">
              <Phone size={16} className="text-muted-foreground shrink-0" />
              <a href={`tel:${client.phone}`} className="text-sm hover:underline" data-testid="client-phone-display">{client.phone}</a>
            </div>
          )}
          {client.company_address && (
            <div className="flex items-center gap-2 border border-border rounded-md px-3 py-2">
              <Buildings size={16} className="text-muted-foreground shrink-0" />
              <span className="text-sm truncate" data-testid="client-address-display">{client.company_address}</span>
            </div>
          )}
        </div>
      </div>

      {/* Phase 258 — Acknowledgment Email override for this client */}
      <div className="mb-6 border border-border rounded-lg p-4" data-testid="client-ack-email-section">
        <AcknowledgmentEmailEditor clientId={clientId} clientLabel={client.company_name || `${client.first_name || ""} ${client.last_name || ""}`.trim()} />
      </div>

      {/* Client Portal Section */}
      <div className="mb-6 border border-border rounded-lg overflow-hidden" data-testid="client-portal-section">
        <ClientEmailSwitch client={client} onChanged={fetchClient} />
        <div className="flex items-center justify-between p-4 bg-muted/20">
          <div className="flex items-center gap-3">
            <Globe size={20} className="text-primary" />
            <div>
              <h2 className="text-sm font-semibold font-[Chivo]">Client Portal</h2>
              <p className="text-[10px] text-muted-foreground mt-0.5">Secure logins for the client's team — requests, emergencies, jobs, quotes, invoices and files</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{client.portal_enabled ? "Enabled" : "Disabled"}</span>
            <Switch checked={client.portal_enabled || false} onCheckedChange={handleTogglePortal} data-testid="client-portal-toggle" />
          </div>
        </div>

        {/* Auto Invoice Toggle */}
        <div className="flex items-center justify-between p-4 border-t border-border bg-muted/10">
          <div className="flex items-center gap-3">
            <Receipt size={20} className="text-amber-500" />
            <div>
              <h2 className="text-sm font-semibold font-[Chivo]">Auto-Invoice</h2>
              <p className="text-[10px] text-muted-foreground mt-0.5">Automatically generate invoices when jobs for this client are completed</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{client.auto_invoice ? "On" : "Off"}</span>
            <Switch checked={client.auto_invoice || false} onCheckedChange={handleToggleAutoInvoice} data-testid="client-auto-invoice-toggle" />
          </div>
        </div>

        {/* Contractor visibility default */}
        <div className="flex items-center justify-between p-4 border-t border-border bg-muted/10" data-testid="client-contractor-default-row">
          <div className="flex items-center gap-3">
            <Eye size={20} className="text-emerald-600" />
            <div>
              <h2 className="text-sm font-semibold font-[Chivo]">Show Contractor Names by Default</h2>
              <p className="text-[10px] text-muted-foreground mt-0.5">Trusted client? Reveal the assigned contractor on every job and emergency in their portal. Individual jobs can still override this.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{client.show_contractor_default ? "Shown" : "Hidden"}</span>
            <Switch checked={client.show_contractor_default || false} onCheckedChange={handleToggleContractorDefault} data-testid="client-contractor-default-toggle" />
          </div>
        </div>

        {/* PO / WO required rule */}
        <div className="flex items-center justify-between p-4 border-t border-border bg-muted/10" data-testid="client-po-required-row">
          <div className="flex items-center gap-3">
            <Hash size={20} className="text-violet-600" />
            <div>
              <h2 className="text-sm font-semibold font-[Chivo]">PO / WO Required for Invoicing</h2>
              {client.parent ? (
                <p className="text-[10px] text-muted-foreground mt-0.5" data-testid="client-po-inherited-note">Inherited from <Link to={`/clients/${client.parent.id}`} className="text-primary hover:underline">{client.parent.name}</Link> — invoices for this sub-client's work are billed there{client.parent.po_required ? ", and a PO/WO number is required on every job before it can be invoiced." : ", which doesn't currently require PO/WO numbers."}</p>
              ) : (
                <p className="text-[10px] text-muted-foreground mt-0.5">Jobs and emergencies can't be invoiced until a client PO/WO number is entered{client.sub_clients?.length ? " — applies to every sub-client's work too" : ""}. Individual jobs can be waived (“No PO/WO issued”).</p>
              )}
            </div>
          </div>
          {client.parent ? (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${client.parent.po_required ? "bg-violet-500/10 text-violet-700" : "bg-muted text-muted-foreground"}`} data-testid="client-po-required-inherited">{client.parent.po_required ? "Required by main client" : "Not required"}</span>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">{client.po_required ? "Required" : "Optional"}</span>
              <Switch checked={client.po_required || false} onCheckedChange={handleTogglePoRequired} data-testid="client-po-required-toggle" />
            </div>
          )}
        </div>

        {client.parent?.po_required && (
          <div className="flex items-center justify-between p-4 border-t border-border bg-amber-500/5" data-testid="client-po-exempt-row">
            <div className="flex items-center gap-3">
              <Prohibit size={20} className="text-amber-600" />
              <div>
                <h2 className="text-sm font-semibold font-[Chivo]">Doesn't Issue PO / WO Numbers</h2>
                <p className="text-[10px] text-muted-foreground mt-0.5">Exempt this sub-client from {client.parent.name}'s PO/WO rule — its jobs can be invoiced (and consolidated) without one.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">{client.po_exempt ? "Exempt" : "Rule applies"}</span>
              <Switch checked={client.po_exempt || false} onCheckedChange={handleTogglePoExempt} data-testid="client-po-exempt-toggle" />
            </div>
          </div>
        )}

        <ClientPortalUsersPanel clientId={clientId} disabled={client.portal_enabled === false} canDelete={["super_admin", "admin"].includes(currentUser?.role)} />
      </div>

      <SubClientsPanel client={client} onChanged={fetchClient} />

      <ClientBrandingPanel clientId={clientId} clientName={client.company_name || [client.first_name, client.last_name].filter(Boolean).join(" ")} />

      <ClientPropertiesPanel clientId={clientId} />

      {/* Client Messages Section */}
      <div className="mb-6 border border-border rounded-lg overflow-hidden" data-testid="client-messages-section">
        <div className="flex items-center justify-between p-4 bg-muted/20 border-b border-border">
          <div className="flex items-center gap-2">
            <ClipboardText size={18} className="text-blue-500" />
            <h2 className="text-sm font-semibold font-[Chivo]">Client Messages</h2>
            {clientMessages.length > 0 && <Badge variant="secondary" className="text-[9px]">{clientMessages.length}</Badge>}
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto p-4 space-y-2" data-testid="admin-client-messages-list">
          {clientMsgLoading ? (
            <div className="text-center py-6 text-muted-foreground text-sm">Loading...</div>
          ) : clientMessages.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-xs text-muted-foreground">No messages with this client yet</p>
            </div>
          ) : (
            clientMessages.map(m => (
              <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`} data-testid={`admin-msg-${m.id}`}>
                <div className={`max-w-[75%] rounded-lg px-3 py-2 ${m.sender === "admin" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  <p className="text-[10px] font-medium mb-0.5">{m.sender_name}</p>
                  <p className="text-xs">{m.text}</p>
                  <p className={`text-[9px] mt-1 ${m.sender === "admin" ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                    {m.created_at ? new Date(m.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="border-t border-border p-3 flex items-center gap-2">
          <Input
            value={adminReply}
            onChange={e => setAdminReply(e.target.value)}
            placeholder="Reply to client..."
            className="flex-1 text-sm"
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendAdminReply(); } }}
            data-testid="admin-reply-input"
          />
          <Button size="sm" onClick={handleSendAdminReply} disabled={sendingReply || !adminReply.trim()} data-testid="admin-send-reply-btn">
            Send
          </Button>
        </div>
      </div>

      {/* Sub-Contacts Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold font-[Chivo]">Contacts</h2>
            <Badge variant="secondary">{contacts.length}</Badge>
          </div>
          <Button size="sm" onClick={() => { setEditingContact(null); setContactForm({ ...EMPTY_CONTACT }); setShowAddContact(true); }} data-testid="add-contact-btn">
            <Plus size={14} className="mr-1" /> Add Contact
          </Button>
        </div>

        {contacts.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-border rounded-lg">
            <User size={36} className="mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-muted-foreground text-sm">No sub-contacts yet</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowAddContact(true)}>Add first contact</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {contacts.map(c => (
              <div key={c.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow" style={{ borderLeftWidth: 4, borderLeftColor: c.color || "#3b82f6" }} data-testid={`contact-card-${c.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-full bg-accent/30 text-accent-foreground flex items-center justify-center text-xs font-bold">
                      {c.first_name?.[0]?.toUpperCase()}{c.last_name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{c.first_name} {c.last_name}</p>
                      {c.job_role && <p className="text-[10px] text-muted-foreground">{c.job_role}</p>}
                    </div>
                  </div>
                  <div className="flex gap-0.5">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditContact(c)} data-testid={`edit-contact-${c.id}`}><PencilSimple size={12} /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => handleDeleteContact(c.id)} data-testid={`delete-contact-${c.id}`}><Trash size={12} /></Button>
                  </div>
                </div>
                {c.email && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1.5">
                    <Envelope size={12} /> <a href={`mailto:${c.email}`} className="hover:underline truncate">{c.email}</a>
                  </div>
                )}
                {c.phone && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                    <Phone size={12} /> <a href={`tel:${c.phone}`} className="hover:underline">{c.phone}</a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documents Section */}
      <div className="mb-6" data-testid="client-linked-folders-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold font-[Chivo]">File Hub Folders</h2>
            <Badge variant="secondary">{linkedFolders.length}</Badge>
          </div>
          <Link to="/files">
            <Button size="sm" variant="outline" data-testid="client-open-hub-btn">
              <FolderIcon size={14} className="mr-1" /> Open File Hub
            </Button>
          </Link>
        </div>

        {linkedFoldersLoading ? (
          <div className="text-center py-6 text-muted-foreground text-sm">Loading folders…</div>
        ) : linkedFolders.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-border rounded-lg">
            <FolderIcon size={32} className="mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-muted-foreground text-sm">No File Hub folders linked to this client yet.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Create a folder in the File Hub and set "Link to client" to this account to collect properties, jobs and media here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {linkedFolders.map(f => (
              <Link key={f.id} to={`/files/${f.id}`}
                    className="group p-3 rounded-lg border border-border bg-card hover:border-primary/60 hover:shadow-sm transition"
                    data-testid={`client-folder-${f.id}`}>
                <div className="flex items-start gap-3">
                  <FolderIcon size={24} className="text-primary shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate">{f.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 capitalize">{(f.kind || "folder").replace("_", " ")}</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-1">
                      {f.file_count || 0} file{(f.file_count || 0) === 1 ? "" : "s"}
                      {f.path ? ` · ${f.path}` : ""}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="mb-6" data-testid="client-documents-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold font-[Chivo]">Documents</h2>
            <Badge variant="secondary">{documents.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && <Button size="sm" variant="outline" onClick={() => setShowHsePack(true)} data-testid="client-hse-pack-btn"><ShieldCheck size={14} className="mr-1" /> HSE pack</Button>}
            <Button size="sm" onClick={() => { setDocFile(null); setDocCategory("other"); setDocNotes(""); setShowUploadDoc(true); }} data-testid="upload-doc-btn">
              <UploadSimple size={14} className="mr-1" /> Upload Document
            </Button>
          </div>
        </div>
        <ClientHsePackDialog open={showHsePack} onOpenChange={setShowHsePack} client={client ? { id: client.id, name: client.company_name || [client.first_name, client.last_name].filter(Boolean).join(" ") } : null} onGenerated={fetchDocuments} />

        {docsLoading ? (
          <div className="text-center py-8 text-muted-foreground text-sm">Loading documents...</div>
        ) : documents.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-border rounded-lg">
            <FolderOpen size={36} className="mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-muted-foreground text-sm">No documents yet</p>
            <p className="text-xs text-muted-foreground mt-1">Upload contracts, agreements, or invoices.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowUploadDoc(true)}>Upload first document</Button>
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/30 border-b border-border">
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">File</th>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Category</th>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Notes</th>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Size</th>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Date</th>
                  <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => (
                  <tr key={doc.id} className="border-b border-border hover:bg-muted/20 transition-colors" data-testid={`doc-row-${doc.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileIcon size={16} className="text-muted-foreground shrink-0" />
                        <span className="font-medium truncate max-w-[200px]">{doc.file_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`text-[10px] ${doc.category === "hse_pack" ? "" : "capitalize"}`}>{doc.category === "hse_pack" ? "HSE pack" : doc.category?.replace(/_/g, " ")}</Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground truncate max-w-[180px]">{doc.notes || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{formatFileSize(doc.size)}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{doc.created_at ? new Date(doc.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <a href={doc.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted transition-colors" data-testid={`download-doc-${doc.id}`}>
                          <DownloadSimple size={14} />
                        </a>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setEditingDoc({ ...doc })} data-testid={`edit-doc-${doc.id}`}><PencilSimple size={14} /></Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={() => handleDeleteDoc(doc.id)} data-testid={`delete-doc-${doc.id}`}><Trash size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Financial Summary */}
      {financial && (financial.total_quoted > 0 || financial.total_requests > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="client-financial-summary">
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Total Quoted</span>
              <ChartBar size={16} className="text-blue-500" />
            </div>
            <p className="text-2xl font-bold font-[Chivo] tracking-tight" data-testid="financial-total-quoted">
              £{financial.total_quoted.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">Across {financial.total_requests} request{financial.total_requests !== 1 ? "s" : ""}</p>
          </div>
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Approved Spend</span>
              <ArrowUp size={16} className="text-emerald-500" />
            </div>
            <p className="text-2xl font-bold font-[Chivo] tracking-tight text-emerald-600 dark:text-emerald-400" data-testid="financial-approved-spend">
              £{financial.approved_spend.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">Client approved or converted</p>
          </div>
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Outstanding</span>
              <ArrowDown size={16} className="text-amber-500" />
            </div>
            <p className="text-2xl font-bold font-[Chivo] tracking-tight text-amber-600 dark:text-amber-400" data-testid="financial-outstanding">
              £{financial.outstanding.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">Pending approval</p>
          </div>
        </div>
      )}

      {/* Lifetime Invoice Billing Summary — only shown when at least one invoice exists */}
      {clientInvoices.length > 0 && (() => {
        const totalOf = (arr) => arr.reduce((s, i) => s + (i.total_inc_vat ?? i.grand_total ?? i.total ?? 0), 0);
        const billable = clientInvoices.filter(i => i.status !== "cancelled");
        const paidArr = clientInvoices.filter(i => i.status === "paid");
        const outstandingArr = clientInvoices.filter(i => ["sent", "overdue"].includes(i.status));
        const overdueArr = clientInvoices.filter(i => i.status === "overdue");
        const fmt = (n) => `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
        return (
          <div className="mb-6" data-testid="client-billing-summary">
            <h2 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Invoice Billing</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="border border-border rounded-lg p-4 bg-muted/20">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Billed</p>
                <p className="text-2xl font-bold font-[Chivo] tracking-tight tabular-nums mt-1" data-testid="billing-billed">{fmt(totalOf(billable))}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{billable.length} invoice{billable.length !== 1 ? "s" : ""}</p>
              </div>
              <div className="border border-emerald-200 dark:border-emerald-800/50 rounded-lg p-4 bg-emerald-50/50 dark:bg-emerald-900/20">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Paid</p>
                <p className="text-2xl font-bold font-[Chivo] tracking-tight tabular-nums text-emerald-600 dark:text-emerald-400 mt-1" data-testid="billing-paid">{fmt(totalOf(paidArr))}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{paidArr.length} settled</p>
              </div>
              <div className="border border-rose-200 dark:border-rose-800/50 rounded-lg p-4 bg-rose-50/50 dark:bg-rose-900/20">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Outstanding</p>
                <p className="text-2xl font-bold font-[Chivo] tracking-tight tabular-nums text-rose-600 dark:text-rose-400 mt-1" data-testid="billing-outstanding">{fmt(totalOf(outstandingArr))}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{outstandingArr.length} sent / overdue</p>
              </div>
              <div className={`border rounded-lg p-4 ${overdueArr.length > 0 ? "border-red-300 dark:border-red-700/60 bg-red-50/60 dark:bg-red-900/20" : "border-border bg-muted/20"}`}>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Overdue</p>
                <p className={`text-2xl font-bold font-[Chivo] tracking-tight tabular-nums mt-1 ${overdueArr.length > 0 ? "text-red-700 dark:text-red-400" : ""}`} data-testid="billing-overdue">{fmt(totalOf(overdueArr))}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{overdueArr.length} past due</p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Activity Timeline */}
      <div className="mb-6" data-testid="client-activity-timeline">
        <h2 className="text-xl font-semibold font-[Chivo] mb-4 flex items-center gap-2">
          <Clock size={20} /> Activity Timeline
        </h2>
        {timelineLoading ? (
          <div className="text-center py-8 text-muted-foreground text-sm">Loading activity...</div>
        ) : timeline.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-border rounded-lg">
            <ClipboardText size={36} className="mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-muted-foreground text-sm">No activity yet</p>
            <p className="text-xs text-muted-foreground mt-1">Job requests, quotes, and jobs linked to this client will appear here.</p>
          </div>
        ) : (
          <div className="relative pl-6 border-l-2 border-border space-y-4">
            {timeline.map((item, idx) => (
              <div key={`${item.type}-${item.id}-${idx}`} className="relative" data-testid={`timeline-item-${item.type}-${idx}`}>
                {/* Dot */}
                <div className={`absolute -left-[25px] top-1 w-3 h-3 rounded-full border-2 border-background ${
                  item.type === "job_request" ? "bg-blue-500" :
                  item.type === "quote" ? "bg-amber-500" : "bg-emerald-500"
                }`} />
                <div className="border border-border rounded-lg p-3 hover:shadow-sm transition-shadow">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0">
                      {item.type === "job_request" && <ClipboardText size={16} className="text-blue-500 mt-0.5 shrink-0" />}
                      {item.type === "quote" && <CurrencyGbp size={16} className="text-amber-500 mt-0.5 shrink-0" />}
                      {item.type === "job" && <Wrench size={16} className="text-emerald-500 mt-0.5 shrink-0" />}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${
                            item.type === "job_request" ? "border-blue-200 text-blue-700 dark:border-blue-800 dark:text-blue-400" :
                            item.type === "quote" ? "border-amber-200 text-amber-700 dark:border-amber-800 dark:text-amber-400" :
                            "border-emerald-200 text-emerald-700 dark:border-emerald-800 dark:text-emerald-400"
                          }`}>
                            {item.type === "job_request" ? "Request" : item.type === "quote" ? "Quote" : "Job"}
                          </Badge>
                          {item.reference && <span className="text-[10px] text-muted-foreground font-mono">{item.reference}</span>}
                          <Badge variant="secondary" className="text-[9px]">{item.status?.replace(/_/g, " ")}</Badge>
                        </div>
                        <p className="text-sm font-medium mt-1 truncate">
                          {item.type === "job_request" && (
                            <Link to={`/job-requests/${item.id}`} className="hover:underline">{item.title || "Untitled Request"}</Link>
                          )}
                          {item.type === "quote" && (
                            <span>{item.vendor_name || "Supplier"} — {item.amount != null ? `£${Number(item.amount).toLocaleString()}` : "No amount"}</span>
                          )}
                          {item.type === "job" && (
                            <Link to={`/jobs/${item.id}`} className="hover:underline">{item.title || "Untitled Job"}</Link>
                          )}
                        </p>
                        {item.type === "job_request" && item.requester && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">Requester: {item.requester}</p>
                        )}
                        {item.type === "job" && item.vendor_name && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">Supplier: {item.vendor_name}</p>
                        )}
                        {item.type === "quote" && item.job_request_ref && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">For: {item.job_request_ref}</p>
                        )}
                      </div>
                    </div>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={showAddContact} onOpenChange={(open) => { if (!open) { setEditingContact(null); setContactForm({ ...EMPTY_CONTACT }); } setShowAddContact(open); }}>
        <DialogContent className="max-w-md" data-testid="contact-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">{editingContact ? "Edit Contact" : "Add Contact"}</DialogTitle>
            <DialogDescription>Sub-contact details for {client.company_name || `${client.first_name} ${client.last_name}`}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddContact} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name *</Label>
                <Input value={contactForm.first_name} onChange={e => setContactForm({ ...contactForm, first_name: e.target.value })} required placeholder="Jane" data-testid="contact-first-name" />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={contactForm.last_name} onChange={e => setContactForm({ ...contactForm, last_name: e.target.value })} required placeholder="Doe" data-testid="contact-last-name" />
              </div>
            </div>
            <div>
              <Label>Job Role</Label>
              <Input value={contactForm.job_role} onChange={e => setContactForm({ ...contactForm, job_role: e.target.value })} placeholder="Site Manager" data-testid="contact-job-role" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input type="email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} placeholder="jane@example.com" data-testid="contact-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="+44 7..." data-testid="contact-phone" />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Tag Color</Label>
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="contact-color-picker">
                {TAG_COLORS.map(c => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setContactForm({ ...contactForm, color: c.value })}
                    className={`w-7 h-7 rounded-full border-2 transition-transform ${contactForm.color === c.value ? "border-foreground scale-110 ring-2 ring-offset-2 ring-offset-background" : "border-transparent"}`}
                    style={{ backgroundColor: c.value }}
                    title={c.label}
                    data-testid={`contact-color-${c.value.replace("#", "")}`}
                  />
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddContact(false)}>Cancel</Button>
              <Button type="submit" disabled={savingContact} data-testid="contact-save-btn">{savingContact ? "Saving..." : editingContact ? "Update Contact" : "Add Contact"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={showEditClient} onOpenChange={setShowEditClient}>
        <DialogContent className="max-w-lg" data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Edit Client</DialogTitle>
            <DialogDescription>Update primary contact details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditClient} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First Name *</Label><Input value={clientForm.first_name || ""} onChange={e => setClientForm({ ...clientForm, first_name: e.target.value })} required /></div>
              <div><Label>Last Name *</Label><Input value={clientForm.last_name || ""} onChange={e => setClientForm({ ...clientForm, last_name: e.target.value })} required /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Company Name</Label><Input value={clientForm.company_name || ""} onChange={e => setClientForm({ ...clientForm, company_name: e.target.value })} /></div>
              <div><Label>Job Role</Label><Input value={clientForm.job_role || ""} onChange={e => setClientForm({ ...clientForm, job_role: e.target.value })} /></div>
            </div>
            <div>
              <Label>Company Address</Label>
              <AddressAutocomplete
                value={clientForm.company_address || ""}
                onChange={v => setClientForm({ ...clientForm, company_address: v })}
                onSelect={(a) => setClientForm(f => ({
                  ...f,
                  company_address: a.formatted_address || f.company_address,
                  company_street: a.street_address || "",
                  company_town: a.town_city || "",
                  company_county: a.county || "",
                  company_postcode: a.postcode || "",
                  company_lat: a.latitude || null,
                  company_lng: a.longitude || null,
                }))}
                placeholder="42 High Street, London, SW1A 1AA"
                data-testid="edit-client-company-address"
              />
              {clientForm.company_street && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Parsed: {[clientForm.company_street, clientForm.company_town, clientForm.company_county, clientForm.company_postcode].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={clientForm.email || ""} onChange={e => setClientForm({ ...clientForm, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={clientForm.phone || ""} onChange={e => setClientForm({ ...clientForm, phone: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditClient(false)}>Cancel</Button>
              <Button type="submit" disabled={savingClient} data-testid="edit-client-save-btn">{savingClient ? "Saving..." : "Update Client"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDoc} onOpenChange={(open) => { if (!open) { setDocFile(null); setDocCategory("other"); setDocNotes(""); } setShowUploadDoc(open); }}>
        <DialogContent className="max-w-md" data-testid="upload-doc-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Upload Document</DialogTitle>
            <DialogDescription>Attach a contract, agreement, invoice, or other document to this client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">File</Label>
              <div
                className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                onClick={() => docFileRef.current?.click()}
                data-testid="doc-dropzone"
              >
                {docFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileIcon size={20} className="text-primary" />
                    <span className="text-sm font-medium">{docFile.name}</span>
                    <span className="text-[10px] text-muted-foreground">({formatFileSize(docFile.size)})</span>
                  </div>
                ) : (
                  <>
                    <UploadSimple size={28} className="mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Click to select a file</p>
                    <p className="text-[10px] text-muted-foreground mt-1">PDF, Word, Excel, Images (max 20MB)</p>
                  </>
                )}
              </div>
              <input ref={docFileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.heic" onChange={e => setDocFile(e.target.files?.[0] || null)} data-testid="doc-file-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Category</Label>
              <Select value={docCategory} onValueChange={setDocCategory}>
                <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="agreement">Agreement</SelectItem>
                  <SelectItem value="invoice">Invoice</SelectItem>
                  <SelectItem value="proposal">Proposal</SelectItem>
                  <SelectItem value="correspondence">Correspondence</SelectItem>
                  <SelectItem value="hse_pack">HSE pack</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Notes (optional)</Label>
              <Textarea value={docNotes} onChange={e => setDocNotes(e.target.value)} rows={2} placeholder="Brief description of this document..." data-testid="doc-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDoc(false)}>Cancel</Button>
            <Button onClick={handleUploadDoc} disabled={uploadingDoc || !docFile} data-testid="doc-upload-submit-btn">
              {uploadingDoc ? <span className="flex items-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> Uploading...</span> : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={!!editingDoc} onOpenChange={(open) => { if (!open) setEditingDoc(null); }}>
        <DialogContent className="max-w-sm" data-testid="edit-doc-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Edit Document</DialogTitle>
            <DialogDescription>{editingDoc?.file_name}</DialogDescription>
          </DialogHeader>
          {editingDoc && (
            <div className="space-y-4">
              <div>
                <Label className="mb-1.5 block">Category</Label>
                <Select value={editingDoc.category} onValueChange={v => setEditingDoc({ ...editingDoc, category: v })}>
                  <SelectTrigger data-testid="edit-doc-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="agreement">Agreement</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="proposal">Proposal</SelectItem>
                    <SelectItem value="correspondence">Correspondence</SelectItem>
                    <SelectItem value="hse_pack">HSE pack</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">Notes</Label>
                <Textarea value={editingDoc.notes || ""} onChange={e => setEditingDoc({ ...editingDoc, notes: e.target.value })} rows={2} data-testid="edit-doc-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDoc(null)}>Cancel</Button>
            <Button onClick={handleUpdateDoc} data-testid="edit-doc-save-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
